import * as React from 'react';
import { cn } from '@/hen-ui/utils';
import {
  degreeCardSimpleVariants,
  type DegreeCardSimpleVariantProps,
} from './degree-card-simple.variants';
import { Button, Heading, LineClamp, Link, Text } from '@/hen-ui/atomics';
import { CardContent, CardFooter, Flex, LogoBox } from '@/hen-ui/composites';
import { CardRoot } from '@/hen-ui/composites/Card/Card';
import { COMPONENT_IDS } from '@/hen-ui/catalog/component-ids';
import { useExpandableClamp } from '@/hen-ui/hooks';
import type {
  DegreeCardCtaProps,
  DegreeCardReadMoreProps,
} from '../DegreeCardDetailed/types';

type DegreeCardSimpleBaseProps = React.HTMLAttributes<HTMLDivElement> &
  DegreeCardSimpleVariantProps & {
    schoolName: string;
    schoolDetails?: string;
    degreeTitle: string;
    schoolLogo: string;
  };

type DegreeCardSimpleDefaultProps = DegreeCardSimpleBaseProps &
  DegreeCardReadMoreProps &
  DegreeCardCtaProps & {
    layout?: 'default';
    /** Heart icon in card header. Default: false */
    favorite?: boolean;
    /** Called when the favorite (heart) button is clicked. Use with favorite=true to wire up. */
    onFavoriteClick?: () => void;
    description: string;
    ctaLabel: string;
  };

type DegreeCardSimpleCompactProps = DegreeCardSimpleBaseProps & {
  layout: 'compact';
  favorite?: never;
  onFavoriteClick?: never;
  description?: never;
  ctaLabel?: never;
  ctaHref?: never;
  onCtaClick?: never;
  readMoreBehavior?: never;
  readMoreHref?: never;
};

export type DegreeCardSimpleProps =
  | DegreeCardSimpleDefaultProps
  | DegreeCardSimpleCompactProps;

/**
 * DegreeCardSimple — compact card for tighter grids (10px radius).
 * Core degree identity + footer with school logo and CTA. Width is fluid.
 * Reuse: Card, Button, Image, Icon, Link, Heading, Text.
 */
export const DegreeCardSimple = React.forwardRef<
  HTMLDivElement,
  DegreeCardSimpleProps
>(
  (
    {
      layout,
      favorite = false,
      onFavoriteClick,
      readMoreBehavior = 'expand',
      schoolName,
      schoolDetails,
      degreeTitle,
      description,
      readMoreHref,
      schoolLogo,
      ctaLabel,
      ctaHref,
      onCtaClick,
      className,
      border,
      ...props
    },
    ref,
  ) => {
    const useExpand = readMoreBehavior === 'expand';
    const descriptionId = React.useId();
    const {
      ref: descriptionRef,
      expanded: descriptionExpanded,
      isTruncated: isDescriptionTruncated,
      toggleExpanded,
    } = useExpandableClamp(description ?? '');

    if (layout === 'compact') {
      return (
        <CardRoot
          ref={ref}
          className={cn(degreeCardSimpleVariants({ border }), className)}
          {...props}
        >
          <CardContent padding='p-b4'>
            <Flex direction='row' align='center' gap='md'>
              <LogoBox size='sm' src={schoolLogo} alt={`${schoolName} logo`} />
              <Heading variant='h6'>{degreeTitle}</Heading>
            </Flex>
          </CardContent>
        </CardRoot>
      );
    }

    return (
      <CardRoot
        ref={ref}
        className={cn(degreeCardSimpleVariants({ border }), className)}
        data-hen-id={COMPONENT_IDS.DegreeCardSimple}
        {...props}
      >
        <CardContent padding='p-b4'>
          <Flex direction='column' align='start' gap='sm'>
            <Flex direction='row' align='start' justify='between' gap='xs'>
              <Text variant='l1'>
                <Text variant='l1' tag='span' weight='bold'>
                  {schoolName}
                </Text>
                {schoolDetails && (
                  <Text variant='l1' tag='span'>
                    <Text variant='l1' tag='span' weight='bold'>
                      {' - '}
                    </Text>
                    {schoolDetails}
                  </Text>
                )}
              </Text>
              {favorite && (
                <Button
                  variant='iconOnly'
                  size='sm'
                  iconBefore='heart'
                  aria-label='Add to favorites'
                  onClick={onFavoriteClick}
                />
              )}
            </Flex>

            <Heading variant='h6'>{degreeTitle}</Heading>

            <LineClamp
              ref={descriptionRef}
              id={descriptionId}
              lines={useExpand ? (descriptionExpanded ? 'none' : 3) : 3}
            >
              <Text variant='l1'>{description}</Text>
            </LineClamp>
            {useExpand && (descriptionExpanded || isDescriptionTruncated) && (
              <Button
                variant='textOnly'
                size='sm'
                onClick={toggleExpanded}
                aria-expanded={descriptionExpanded}
                aria-controls={descriptionId}
              >
                {descriptionExpanded ? 'Read less' : 'Read More'}
              </Button>
            )}
            {!useExpand && readMoreHref && (
              <Link
                variant='primary'
                underline='always'
                href={readMoreHref}
                size='sm'
                weight='bold'
                className='self-start'
              >
                Read More
              </Link>
            )}
          </Flex>
        </CardContent>

        <CardFooter background='bg-white' padding='px-b4 pb-b4' rounded='md'>
          <Flex direction='row' align='center' justify='between' gap='sm'>
            <LogoBox size='sm' src={schoolLogo} alt={`${schoolName} logo`} />
            {ctaHref?.trim() ? (
              <Button
                variant='primaryFill'
                size='sm'
                href={ctaHref}
                onClick={onCtaClick}
              >
                {ctaLabel}
              </Button>
            ) : onCtaClick ? (
              <Button
                type='button'
                variant='primaryFill'
                size='sm'
                onClick={onCtaClick}
              >
                {ctaLabel}
              </Button>
            ) : null}
          </Flex>
        </CardFooter>
      </CardRoot>
    );
  },
);

DegreeCardSimple.displayName = 'DegreeCardSimple';
